import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus, UserPlus, CheckCircle2, Droplets, Coins, Camera, Crown, Sparkles, ListChecks, TrendingUp, PartyPopper } from "lucide-react";
import { DAYS_PT, formatBRL, todayDow, todayISO } from "@/lib/api";
import ChildDialog from "@/components/ChildDialog";
import PhotoLightbox from "@/components/PhotoLightbox";

function BigNumber({ value, suffix, color = "text-slate-900" }) {
    return (
        <div className={`text-4xl font-extrabold leading-none ${color}`} style={{ fontFamily: "Outfit" }}>
            {value}
            {suffix && <span className="text-xl text-slate-400">{suffix}</span>}
        </div>
    );
}

export default function ParentDashboard() {
    const { children } = useApp();
    const [perChild, setPerChild] = useState({}); // child_id -> {tasks, done, pending, bath, earnings}
    const [summary, setSummary] = useState({});
    const [bath, setBath] = useState(null);
    const [childDlg, setChildDlg] = useState(false);
    const [editingChild, setEditingChild] = useState(null);
    const [lightboxCheckin, setLightboxCheckin] = useState(null);

    const dow = todayDow();
    const today = todayISO();

    const load = async () => {
        const [sumRes, bathRes] = await Promise.all([
            api.get("/payments/summary"),
            api.get("/bath/status"),
        ]);
        setSummary(sumRes.data || {});
        setBath(bathRes.data || null);

        const result = {};
        await Promise.all(children.map(async (c) => {
            const [tasksRes, checkinsRes] = await Promise.all([
                api.get("/tasks", { params: { child_id: c.id } }),
                api.get("/checkins", { params: { child_id: c.id, date: today } }),
            ]);
            const tasks = tasksRes.data.filter((t) => t.active && (!t.recurring_days?.length || t.recurring_days.includes(dow)));
            const doneIds = new Set(checkinsRes.data.map((x) => x.task_id));
            const taskTitleById = Object.fromEntries(tasksRes.data.map((t) => [t.id, t.title]));
            result[c.id] = {
                total: tasks.length,
                done: tasks.filter((t) => doneIds.has(t.id)).length,
                photoRequiredPending: tasks.filter((t) => !doneIds.has(t.id) && t.photo_required).length,
                checkinsWithPhoto: checkinsRes.data
                    .filter((x) => x.photo_path)
                    .slice(-3)
                    .map((x) => ({ ...x, task_title: taskTitleById[x.task_id], child_name: c.name })),
            };
        }));
        setPerChild(result);
    };

    useEffect(() => { if (children.length) load(); /* eslint-disable-next-line */ }, [children]);

    if (!children.length) {
        return (
            <div className="rise">
                <div className="text-xs uppercase tracking-widest text-[#8a7968]">Começar</div>
                <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                    Vamos adicionar o primeiro filho <Sparkles className="inline h-7 w-7 text-[#E5A93C]" />
                </h1>
                <p className="text-slate-600 mt-2 max-w-xl">
                    Crie um perfil com nome, cor, apelido e PIN. Depois é só cadastrar as tarefas e deixar seus filhos entrarem no modo criança.
                </p>
                <div className="mt-8">
                    <Card className="p-8 border-[#E2DCD5] max-w-md">
                        <div className="h-14 w-14 rounded-2xl bg-[#D96B43] text-white grid place-items-center">
                            <UserPlus className="h-6 w-6" />
                        </div>
                        <h3 className="mt-4 text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Adicionar filho</h3>
                        <p className="text-sm text-slate-500 mt-1">Leva menos de 1 minuto.</p>
                        <Button onClick={() => { setEditingChild(null); setChildDlg(true); }} data-testid="add-first-child-button" className="mt-5 rounded-full bg-[#D96B43] hover:bg-[#C25933] text-white">
                            <Plus className="h-4 w-4 mr-1.5" /> Adicionar filho
                        </Button>
                    </Card>
                </div>
                <ChildDialog open={childDlg} onOpenChange={setChildDlg} child={editingChild} />
            </div>
        );
    }

    const totalPending = Object.values(summary).reduce((a, s) => a + (s.pending || 0), 0);
    const totalPaid = Object.values(summary).reduce((a, s) => a + (s.paid || 0), 0);
    const totalExpected = Object.values(perChild).reduce((a, s) => a + (s.total || 0), 0);
    const totalDone = Object.values(perChild).reduce((a, s) => a + (s.done || 0), 0);
    const overallPct = totalExpected ? Math.round((totalDone / totalExpected) * 100) : 0;
    const firstId = bath?.today_first_child_id;
    const expectedId = bath?.expected_first_child_id;
    const childrenMap = Object.fromEntries(children.map((c) => [c.id, c]));

    return (
        <div className="space-y-7">
            <div className="flex flex-wrap items-end justify-between gap-4 rise">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#8a7968]">
                        Hoje — {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                        Como está a <span className="text-[#D96B43]">galera</span> hoje?
                    </h1>
                </div>
                <Button onClick={() => { setEditingChild(null); setChildDlg(true); }} variant="outline" className="rounded-full" data-testid="add-child-button">
                    <UserPlus className="h-4 w-4 mr-1.5" /> Adicionar filho
                </Button>
            </div>

            {/* big summary row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rise rise-1">
                <Card className="relative overflow-hidden p-6 border-[#E2DCD5]" data-testid="summary-progress">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[#8a7968]">Progresso da família</div>
                            <BigNumber value={overallPct} suffix="%" />
                            <div className="text-xs text-slate-500 mt-1">{totalDone} de {totalExpected} tarefas</div>
                        </div>
                        <div className="h-11 w-11 rounded-xl bg-[#FFF1EA] text-[#D96B43] grid place-items-center">
                            <ListChecks className="h-5 w-5" />
                        </div>
                    </div>
                    <div className="mt-4"><Progress value={overallPct} className="h-2 bg-[#F3EFEA]" /></div>
                </Card>
                <Card className="p-6 border-amber-200 bg-amber-50/50" data-testid="summary-pending">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-amber-800">A pagar pros filhos</div>
                            <BigNumber value={formatBRL(totalPending)} color="text-amber-900" />
                            <div className="text-xs text-amber-700 mt-1">Já pago no total: {formatBRL(totalPaid)}</div>
                        </div>
                        <div className="h-11 w-11 rounded-xl bg-amber-200/70 text-amber-800 grid place-items-center">
                            <Coins className="h-5 w-5" />
                        </div>
                    </div>
                    <Link to="/pagamentos" className="inline-flex mt-4 items-center text-sm font-semibold text-amber-800 hover:underline" data-testid="go-to-payments">
                        Gerenciar pagamentos →
                    </Link>
                </Card>
                <Card className="p-6 border-sky-200 bg-sky-50/50" data-testid="summary-bath">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-sky-800">Primeiro no banho</div>
                            <div className="text-2xl font-bold mt-1 text-sky-900" style={{ fontFamily: "Outfit" }}>
                                {firstId ? childrenMap[firstId]?.name
                                    : expectedId ? `Vez do ${childrenMap[expectedId]?.name}`
                                    : "A definir"}
                            </div>
                            <div className="text-xs text-sky-700 mt-1">
                                {bath?.today_entries?.length || 0}/{children.length} banhos marcados hoje
                            </div>
                        </div>
                        <div className="h-11 w-11 rounded-xl bg-sky-200/70 text-sky-800 grid place-items-center">
                            <Droplets className="h-5 w-5" />
                        </div>
                    </div>
                    <Link to="/banho" className="inline-flex mt-4 items-center text-sm font-semibold text-sky-800 hover:underline" data-testid="go-to-bath">
                        Ir pro banho →
                    </Link>
                </Card>
            </div>

            {/* per-child cards */}
            <section>
                <h2 className="text-xl font-semibold mb-3 rise rise-2" style={{ fontFamily: "Outfit" }}>Panorama por filho</h2>
                <div className="grid gap-5 md:grid-cols-2 rise rise-3">
                    {children.map((c) => {
                        const stats = perChild[c.id] || { total: 0, done: 0, photoRequiredPending: 0, checkinsWithPhoto: [] };
                        const earn = summary[c.id] || { pending: 0, paid: 0 };
                        const bathEntry = bath?.today_entries?.find((e) => e.child_id === c.id);
                        const bathIsFirst = bathEntry?.is_first;
                        const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
                        return (
                            <Card key={c.id} className="p-6 border-[#E2DCD5] hover:shadow-md" data-testid={`parent-child-card-${c.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="relative">
                                            <div className="h-14 w-14 rounded-2xl grid place-items-center text-white text-xl font-bold shadow-sm" style={{ backgroundColor: c.avatar_color }}>
                                                {c.name?.[0]?.toUpperCase()}
                                            </div>
                                            {bathIsFirst && (
                                                <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-amber-400 text-white grid place-items-center shadow-sm" title="Primeiro no banho hoje">
                                                    <Crown className="h-3.5 w-3.5" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-bold text-lg truncate" style={{ fontFamily: "Outfit" }}>{c.name}</div>
                                            <div className="text-xs text-slate-500">apelido: <span className="font-mono">{c.username}</span></div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => { setEditingChild(c); setChildDlg(true); }}
                                        className="text-xs text-slate-400 hover:text-[#D96B43]"
                                        data-testid={`edit-child-${c.id}`}
                                    >
                                        editar
                                    </button>
                                </div>

                                <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                                    <div className="rounded-xl bg-[#F3EFEA] p-3">
                                        <div className="text-xs uppercase tracking-widest text-[#8a7968]">Hoje</div>
                                        <div className="text-xl font-bold mt-0.5" style={{ fontFamily: "Outfit" }}>
                                            {stats.done}<span className="text-slate-400 text-sm">/{stats.total}</span>
                                        </div>
                                    </div>
                                    <div className="rounded-xl bg-sky-50 p-3">
                                        <div className="text-xs uppercase tracking-widest text-sky-800">Banho</div>
                                        <div className="text-sm font-semibold mt-0.5 text-sky-900">
                                            {bathEntry ? (bathIsFirst ? "1º ★" : "feito") : "pendente"}
                                        </div>
                                    </div>
                                    <div className="rounded-xl bg-amber-50 p-3">
                                        <div className="text-xs uppercase tracking-widest text-amber-800">A receber</div>
                                        <div className="text-sm font-bold mt-0.5 text-amber-900">
                                            {formatBRL(earn.pending)}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                                        <span>Tarefas de hoje</span>
                                        <span>{pct}%</span>
                                    </div>
                                    <Progress value={pct} className="h-2 bg-slate-100" />
                                </div>

                                {stats.photoRequiredPending > 0 && (
                                    <div className="mt-3 chip bg-[#FFF1EA] text-[#C25933] border border-[#F2CBB6]">
                                        <Camera className="h-3 w-3" /> {stats.photoRequiredPending} tarefa(s) com foto obrigatória pendente
                                    </div>
                                )}

                                {stats.checkinsWithPhoto.length > 0 && (
                                    <div className="mt-4">
                                        <div className="text-xs text-slate-500 mb-2">Últimos check-ins com foto</div>
                                        <div className="flex gap-2">
                                            {stats.checkinsWithPhoto.map((ci) => (
                                                <button
                                                    key={ci.id}
                                                    onClick={() => setLightboxCheckin(ci)}
                                                    className="h-14 w-14 rounded-lg overflow-hidden border border-[#E2DCD5] hover:ring-2 hover:ring-[#D96B43] transition"
                                                    data-testid={`checkin-photo-${ci.id}`}
                                                >
                                                    <img
                                                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${ci.photo_path}`}
                                                        alt=""
                                                        className="h-full w-full object-cover"
                                                        crossOrigin="use-credentials"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-5 flex gap-2 flex-wrap">
                                    <Link to="/tarefas"><Button size="sm" variant="outline" className="rounded-full" data-testid={`view-tasks-${c.id}`}><ListChecks className="h-4 w-4 mr-1" /> Tarefas</Button></Link>
                                    <Link to="/desempenho"><Button size="sm" variant="outline" className="rounded-full" data-testid={`view-perf-${c.id}`}><TrendingUp className="h-4 w-4 mr-1" /> Semana</Button></Link>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </section>

            {totalDone > 0 && totalDone === totalExpected && (
                <Card className="p-6 border-emerald-200 bg-emerald-50/50 flex items-center gap-4 rise" data-testid="family-done-banner">
                    <PartyPopper className="h-8 w-8 text-emerald-700" />
                    <div>
                        <div className="font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>Dia completo!</div>
                        <div className="text-sm text-emerald-800">Todo mundo fechou as tarefas de hoje. Mandaram muito bem!</div>
                    </div>
                </Card>
            )}

            <ChildDialog open={childDlg} onOpenChange={setChildDlg} child={editingChild} />
            <PhotoLightbox
                open={!!lightboxCheckin}
                onOpenChange={(v) => !v && setLightboxCheckin(null)}
                checkin={lightboxCheckin}
                onInvalidated={load}
            />
        </div>
    );
}
